import type { WeatherCondition } from '@frisk/shared';

/**
 * Weather glyphs in the same hand-drawn style as the nav icons — same stroke
 * weight, same `currentColor` contract, no icon dependency for nine shapes.
 *
 * `isDay` only changes the clear and partly-cloudy cases: a sun at 3am reads
 * as broken, and nothing else about a cloud differs after dark.
 */
export function WeatherIcon({
  condition,
  isDay = true,
  size = 40,
}: {
  condition: WeatherCondition;
  isDay?: boolean;
  size?: number;
}) {
  const base = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  const cloud = <path d="M7 18h9.5a3.5 3.5 0 0 0 .3-7 5 5 0 0 0-9.5-1.2A3.9 3.9 0 0 0 7 18Z" />;
  const sun = (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 3v1.8M12 19.2V21M4.2 12H3M21 12h-1.2M6.3 6.3l-1-1M18.7 18.7l1 1M17.7 6.3l1-1M5.3 18.7l-1 1" />
    </>
  );
  const moon = <path d="M19 14.5A7.5 7.5 0 0 1 9.5 5a7.5 7.5 0 1 0 9.5 9.5Z" />;

  switch (condition) {
    case 'clear':
      return <svg {...base}>{isDay ? sun : moon}</svg>;
    case 'partly':
      return (
        <svg {...base}>
          {isDay ? (
            <>
              <circle cx="8.5" cy="8" r="3" />
              <path d="M8.5 2.6v1.2M3.1 8H4.3M4.7 4.2l.9.9M12.3 4.2l-.9.9" />
            </>
          ) : (
            <path d="M13.5 9.5A5 5 0 0 1 8 4a5 5 0 1 0 5.5 5.5Z" />
          )}
          {cloud}
        </svg>
      );
    case 'cloudy':
      return <svg {...base}>{cloud}</svg>;
    case 'fog':
      return (
        <svg {...base}>
          <path d="M4 9h16M6 13h12M4 17h16" />
        </svg>
      );
    case 'drizzle':
      return (
        <svg {...base}>
          <path d="M7 15h9.5a3.5 3.5 0 0 0 .3-7 5 5 0 0 0-9.5-1.2A3.9 3.9 0 0 0 7 15Z" />
          <path d="M9 18.5v1M13 18.5v1M17 18.5v1" />
        </svg>
      );
    case 'rain':
      return (
        <svg {...base}>
          <path d="M7 15h9.5a3.5 3.5 0 0 0 .3-7 5 5 0 0 0-9.5-1.2A3.9 3.9 0 0 0 7 15Z" />
          <path d="M9.5 18l-1 2.5M13.5 18l-1 2.5M17.5 18l-1 2.5" />
        </svg>
      );
    case 'showers':
      return (
        <svg {...base}>
          <path d="M7 14h9.5a3.5 3.5 0 0 0 .3-7 5 5 0 0 0-9.5-1.2A3.9 3.9 0 0 0 7 14Z" />
          <path d="M10 17l-1.4 3.4M15 17l-1.4 3.4" />
        </svg>
      );
    case 'snow':
      return (
        <svg {...base}>
          <path d="M7 14h9.5a3.5 3.5 0 0 0 .3-7 5 5 0 0 0-9.5-1.2A3.9 3.9 0 0 0 7 14Z" />
          <path d="M9.5 18.2v.1M13 20v.1M16.5 18.2v.1M9.5 21v.1M16.5 21v.1" />
        </svg>
      );
    case 'thunder':
      return (
        <svg {...base}>
          <path d="M7 14h9.5a3.5 3.5 0 0 0 .3-7 5 5 0 0 0-9.5-1.2A3.9 3.9 0 0 0 7 14Z" />
          <path d="M13 16.5l-3 3.5h3l-1 3" />
        </svg>
      );
  }
}
